/**
 * ProcessBuilderWizard Component
 *
 * Multi-step wizard for creating/editing processes.
 * Uses Standard Component Library throughout.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Save,
  X,
  Plus,
  Trash2,
  AlertCircle,
  Info,
  GitBranch,
  Zap,
  Clock,
  ArrowUp,
  ArrowDown,
  CheckCircle
} from 'lucide-react';
import {
  StandardButton,
  StandardInput,
  StandardSelect,
  StandardTextarea,
  StandardCheckbox,
  StandardCard,
  useToast
} from '../../standard';
import { CriteriaBuilder } from './CriteriaBuilder';
import { ActionList } from './ActionConfigurator';
import * as processBuilderStorageRouter from '../../../services/processBuilderStorageRouter';
import type {
  Process,
  ProcessTriggerType,
  ProcessCriteriaGroupInput,
  ProcessConditionInput,
  ProcessActionInput,
  ScheduledActionInput,
  CreateProcessPayload,
  ScheduleType
} from '../../../types/processBuilder';

// ============================================
// INTERFACES
// ============================================

interface ProcessBuilderWizardProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  editProcessId?: string | null;
  processId?: string | null;
}

interface WizardFormData {
  // Step 1: Basic Info
  name: string;
  description: string;
  objectType: string;
  triggerType: ProcessTriggerType;
  category: string;

  // Step 2: Entry Criteria
  hasEntryCriteria: boolean;
  entryCriteria: ProcessCriteriaGroupInput;

  // Step 3: Decision Groups
  criteriaGroups: ProcessCriteriaGroupInput[];

  // Step 4: Immediate Actions (per criteria group)
  immediateActions: Record<string, ProcessActionInput[]>;

  // Step 5: Scheduled Actions (per criteria group)
  scheduledActions: Record<string, ScheduledActionInput[]>;

  // Step 6: Settings
  reevaluateOnUpdate: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const WIZARD_STEPS = [
  { id: 1, name: 'Basic Info', description: 'Name and trigger' },
  { id: 2, name: 'Entry Criteria', description: 'When to run' },
  { id: 3, name: 'Decision Groups', description: 'If/Then branches' },
  { id: 4, name: 'Immediate Actions', description: 'What happens' },
  { id: 5, name: 'Scheduled Actions', description: 'Time-based actions' },
  { id: 6, name: 'Review', description: 'Review & activate' }
];

const OBJECT_TYPE_OPTIONS = [
  { value: '', label: 'Select object type...' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Contract', label: 'Contract' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Account', label: 'Account' },
  { value: 'Task', label: 'Task' }
];

const TRIGGER_TYPE_OPTIONS = [
  { value: 'record_created', label: 'Record is Created' },
  { value: 'record_updated', label: 'Record is Updated' },
  { value: 'record_created_or_updated', label: 'Record is Created or Updated' },
  { value: 'platform_event', label: 'Platform Event' },
  { value: 'invocable', label: 'Invocable (Called from Other Process)' }
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'No Category' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Service', label: 'Service' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Finance', label: 'Finance' }
];

const SCHEDULE_TYPE_OPTIONS = [
  { value: 'days_after', label: 'Days After' },
  { value: 'days_before', label: 'Days Before' },
  { value: 'hours_after', label: 'Hours After' },
  { value: 'hours_before', label: 'Hours Before' },
  { value: 'minutes_after', label: 'Minutes After' }
];

const DATE_FIELD_OPTIONS: Record<string, { value: string; label: string }[]> = {
  Lead: [
    { value: 'CreatedDate', label: 'Created Date' },
    { value: 'LastModifiedDate', label: 'Last Modified Date' }
  ],
  Opportunity: [
    { value: 'CreatedDate', label: 'Created Date' },
    { value: 'CloseDate', label: 'Close Date' }
  ],
  Case: [
    { value: 'CreatedDate', label: 'Created Date' },
    { value: 'ClosedDate', label: 'Closed Date' }
  ],
  Contract: [
    { value: 'StartDate', label: 'Start Date' },
    { value: 'EndDate', label: 'End Date' }
  ],
  Contact: [
    { value: 'CreatedDate', label: 'Created Date' }
  ],
  default: [
    { value: 'CreatedDate', label: 'Created Date' },
    { value: 'LastModifiedDate', label: 'Last Modified Date' }
  ]
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getInitialFormData = (): WizardFormData => ({
  name: '',
  description: '',
  objectType: '',
  triggerType: 'record_created',
  category: '',
  hasEntryCriteria: false,
  entryCriteria: {
    name: 'Entry Criteria',
    conditionLogic: 'AND',
    conditions: [],
    order: 0
  },
  criteriaGroups: [],
  immediateActions: {},
  scheduledActions: {},
  reevaluateOnUpdate: false
});

const getDateFieldOptions = (objectType: string) => {
  return DATE_FIELD_OPTIONS[objectType] || DATE_FIELD_OPTIONS.default;
};

// ============================================
// STEP INDICATOR COMPONENT
// ============================================

const StepIndicator: React.FC<{
  steps: typeof WIZARD_STEPS;
  currentStep: number;
  onStepClick?: (step: number) => void;
}> = ({ steps, currentStep, onStepClick }) => (
  <nav className="flex items-center justify-center mb-8">
    <ol className="flex items-center space-x-2">
      {steps.map((step, index) => {
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;

        return (
          <li key={step.id} className="flex items-center">
            <button
              onClick={() => onStepClick && step.id < currentStep && onStepClick(step.id)}
              disabled={step.id > currentStep}
              className={`flex items-center ${step.id < currentStep ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isCurrent
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {isCompleted ? <Check size={16} /> : step.id}
              </span>
              <span
                className={`hidden sm:block ml-2 text-sm font-medium ${
                  isCurrent
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {step.name}
              </span>
            </button>
            {index < steps.length - 1 && (
              <div
                className={`w-8 sm:w-12 h-0.5 mx-2 ${
                  isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

// ============================================
// DECISION GROUP EDITOR
// ============================================

const DecisionGroupEditor: React.FC<{
  groups: ProcessCriteriaGroupInput[];
  onChange: (groups: ProcessCriteriaGroupInput[]) => void;
  objectType: string;
}> = ({ groups, onChange, objectType }) => {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const addGroup = () => {
    const newGroup: ProcessCriteriaGroupInput = {
      id: generateId('cg'),
      name: `Criteria Group ${groups.length + 1}`,
      conditionLogic: 'AND',
      conditions: [],
      order: groups.length
    };
    onChange([...groups, newGroup]);
    setExpandedGroup(newGroup.id!);
  };

  const removeGroup = (index: number) => {
    onChange(groups.filter((_, i) => i !== index));
  };

  const updateGroup = (index: number, updates: Partial<ProcessCriteriaGroupInput>) => {
    const updated = [...groups];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const moveGroup = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === groups.length - 1) return;

    const updated = [...groups];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {groups.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          <GitBranch className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No decision groups defined</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
            Add groups to create if/then branches in your process
          </p>
          <StandardButton
            variant="primary"
            onClick={addGroup}
            leftIcon={<Plus size={16} />}
          >
            Add Decision Group
          </StandardButton>
        </div>
      ) : (
        <>
          {groups.map((group, index) => (
            <StandardCard key={group.id || index} className="overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id!)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 flex items-center justify-center font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {group.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {group.conditions.length} condition{group.conditions.length !== 1 ? 's' : ''} • {group.conditionLogic}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StandardButton
                    size="xs"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); moveGroup(index, 'up'); }}
                    disabled={index === 0}
                  >
                    <ArrowUp size={14} />
                  </StandardButton>
                  <StandardButton
                    size="xs"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); moveGroup(index, 'down'); }}
                    disabled={index === groups.length - 1}
                  >
                    <ArrowDown size={14} />
                  </StandardButton>
                  <StandardButton
                    size="xs"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); removeGroup(index); }}
                  >
                    <Trash2 size={14} className="text-red-500" />
                  </StandardButton>
                </div>
              </div>

              {expandedGroup === group.id && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <StandardInput
                        label="Group Name"
                        value={group.name}
                        onChange={(e) => updateGroup(index, { name: e.target.value })}
                        placeholder="Name for this branch"
                      />
                      <StandardInput
                        label="Description"
                        value={group.description || ''}
                        onChange={(e) => updateGroup(index, { description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>

                    <CriteriaBuilder
                      conditions={group.conditions}
                      onChange={(conditions) => updateGroup(index, { conditions })}
                      conditionLogic={group.conditionLogic}
                      onLogicChange={(logic) => updateGroup(index, { conditionLogic: logic })}
                      customLogic={group.customLogic}
                      onCustomLogicChange={(logic) => updateGroup(index, { customLogic: logic })}
                      objectType={objectType}
                      title="Conditions"
                      description="Define when this branch should execute"
                    />

                    <StandardCheckbox
                      label="Stop processing other groups if this one matches"
                      checked={group.stopProcessing || false}
                      onChange={(checked) => updateGroup(index, { stopProcessing: checked })}
                    />
                  </div>
                </div>
              )}
            </StandardCard>
          ))}

          <StandardButton
            variant="secondary"
            onClick={addGroup}
            leftIcon={<Plus size={16} />}
          >
            Add Decision Group
          </StandardButton>
        </>
      )}
    </div>
  );
};

// ============================================
// SCHEDULED ACTION EDITOR
// ============================================

const ScheduledActionEditor: React.FC<{
  scheduledActions: ScheduledActionInput[];
  onChange: (actions: ScheduledActionInput[]) => void;
  objectType: string;
}> = ({ scheduledActions, onChange, objectType }) => {
  const [expandedAction, setExpandedAction] = useState<string | null>(null);

  const addScheduledAction = () => {
    const newAction: ScheduledActionInput = {
      id: generateId('sa'),
      name: `Scheduled Action ${scheduledActions.length + 1}`,
      scheduleType: 'days_after',
      scheduleValue: 1,
      baseDateField: 'CreatedDate',
      actions: [],
      isActive: true
    };
    onChange([...scheduledActions, newAction]);
    setExpandedAction(newAction.id!);
  };

  const removeScheduledAction = (index: number) => {
    onChange(scheduledActions.filter((_, i) => i !== index));
  };

  const updateScheduledAction = (index: number, updates: Partial<ScheduledActionInput>) => {
    const updated = [...scheduledActions];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {scheduledActions.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          <Clock className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No scheduled actions</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
            Add time-based actions that execute after a delay
          </p>
          <StandardButton
            variant="primary"
            onClick={addScheduledAction}
            leftIcon={<Plus size={16} />}
          >
            Add Scheduled Action
          </StandardButton>
        </div>
      ) : (
        <>
          {scheduledActions.map((sa, index) => (
            <StandardCard key={sa.id || index} className="overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => setExpandedAction(expandedAction === sa.id ? null : sa.id!)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Clock size={20} className="text-purple-700 dark:text-purple-300" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {sa.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {sa.scheduleValue} {sa.scheduleType.replace(/_/g, ' ')} {sa.baseDateField}
                      {' • '}{sa.actions.length} action{sa.actions.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <StandardButton
                  size="xs"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); removeScheduledAction(index); }}
                >
                  <Trash2 size={14} className="text-red-500" />
                </StandardButton>
              </div>

              {expandedAction === sa.id && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <div className="space-y-4">
                    <StandardInput
                      label="Name"
                      value={sa.name}
                      onChange={(e) => updateScheduledAction(index, { name: e.target.value })}
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <StandardInput
                        label="Value"
                        type="number"
                        value={String(sa.scheduleValue)}
                        onChange={(e) => updateScheduledAction(index, { scheduleValue: parseInt(e.target.value) || 0 })}
                      />
                      <StandardSelect
                        label="Schedule Type"
                        value={sa.scheduleType}
                        onChange={(e) => updateScheduledAction(index, { scheduleType: e.target.value as ScheduleType })}
                        options={SCHEDULE_TYPE_OPTIONS}
                      />
                      <StandardSelect
                        label="Base Date Field"
                        value={sa.baseDateField}
                        onChange={(e) => updateScheduledAction(index, { baseDateField: e.target.value })}
                        options={getDateFieldOptions(objectType)}
                      />
                    </div>

                    <ActionList
                      actions={sa.actions}
                      onChange={(actions) => updateScheduledAction(index, { actions })}
                      objectType={objectType}
                      title="Actions"
                      description="Actions to execute at the scheduled time"
                    />
                  </div>
                </div>
              )}
            </StandardCard>
          ))}

          <StandardButton
            variant="secondary"
            onClick={addScheduledAction}
            leftIcon={<Plus size={16} />}
          >
            Add Scheduled Action
          </StandardButton>
        </>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ProcessBuilderWizard: React.FC<ProcessBuilderWizardProps> = ({
  isOpen,
  onClose,
  onSave,
  onCancel,
  editProcessId,
  processId
}) => {
  const resolvedProcessId = editProcessId || processId;
  const { success, error } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WizardFormData>(getInitialFormData());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCriteriaGroup, setSelectedCriteriaGroup] = useState<string>('');

  useEffect(() => {
    if (!resolvedProcessId) {
      setFormData(getInitialFormData());
      setCurrentStep(1);
      return;
    }
    let cancelled = false;
    processBuilderStorageRouter.getProcessAsync(resolvedProcessId).then(process => {
      if (cancelled || !process) return;
      setFormData({
        name: process.name,
        description: process.description,
        objectType: process.objectType,
        triggerType: process.triggerType,
        category: process.category || '',
        hasEntryCriteria: !!process.entryCriteria,
        entryCriteria: process.entryCriteria || {
          name: 'Entry Criteria',
          conditionLogic: 'AND',
          conditions: [],
          order: 0
        },
        criteriaGroups: process.criteriaGroups,
        immediateActions: process.immediateActions,
        scheduledActions: process.scheduledActions,
        reevaluateOnUpdate: process.reevaluateOnUpdate || false
      });
      if (process.criteriaGroups.length > 0) {
        setSelectedCriteriaGroup(process.criteriaGroups[0].id || '');
      }
    });
    return () => { cancelled = true; };
  }, [resolvedProcessId, isOpen]);

  // Update selected criteria group when groups change
  useEffect(() => {
    if (formData.criteriaGroups.length > 0 && !selectedCriteriaGroup) {
      setSelectedCriteriaGroup(formData.criteriaGroups[0].id || '');
    } else if (formData.criteriaGroups.length === 0) {
      setSelectedCriteriaGroup('');
    }
  }, [formData.criteriaGroups]);

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = () => {
    handleSave(false);
  };

  const handleActivate = () => {
    handleSave(true);
  };

  const handleSave = async (activate: boolean) => {
    setIsLoading(true);
    try {
      const payload: CreateProcessPayload = {
        name: formData.name,
        description: formData.description,
        objectType: formData.objectType,
        triggerType: formData.triggerType,
        category: formData.category || undefined,
        isActive: activate,
        entryCriteria: formData.hasEntryCriteria ? formData.entryCriteria : undefined,
        reevaluateOnUpdate: formData.reevaluateOnUpdate,
        criteriaGroups: formData.criteriaGroups,
        immediateActions: formData.immediateActions,
        scheduledActions: formData.scheduledActions
      };
      if (resolvedProcessId) {
        await processBuilderStorageRouter.updateProcessAsync({ id: resolvedProcessId, ...payload });
        success('Process Updated', `${formData.name} has been updated`);
      } else {
        await processBuilderStorageRouter.createProcessAsync(payload);
        success('Process Created', `${formData.name} has been created`);
      }
      onSave?.();
      onClose?.();
    } catch (err) {
      error('Error', 'Failed to save the process');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onCancel?.();
    onClose?.();
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!formData.name && !!formData.objectType;
      case 2:
        return true; // Entry criteria is optional
      case 3:
        return formData.criteriaGroups.length > 0;
      default:
        return true;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Basic Information
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Start by naming your process and selecting what triggers it
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <StandardInput
                label="Process Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter process name"
                required
              />
              <StandardSelect
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                options={CATEGORY_OPTIONS}
              />
            </div>

            <StandardTextarea
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this process does"
              rows={3}
            />

            <div className="grid grid-cols-2 gap-4">
              <StandardSelect
                label="Object Type"
                value={formData.objectType}
                onChange={(e) => setFormData({ ...formData, objectType: e.target.value })}
                options={OBJECT_TYPE_OPTIONS}
                required
              />
              <StandardSelect
                label="Trigger Type"
                value={formData.triggerType}
                onChange={(e) => setFormData({ ...formData, triggerType: e.target.value as ProcessTriggerType })}
                options={TRIGGER_TYPE_OPTIONS}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Entry Criteria
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Define when this process should run (optional)
              </p>
            </div>

            <StandardCheckbox
              label="Only run when specific criteria are met"
              checked={formData.hasEntryCriteria}
              onChange={(checked) => setFormData({ ...formData, hasEntryCriteria: checked })}
            />

            {formData.hasEntryCriteria && (
              <CriteriaBuilder
                conditions={formData.entryCriteria.conditions}
                onChange={(conditions) =>
                  setFormData({
                    ...formData,
                    entryCriteria: { ...formData.entryCriteria, conditions }
                  })
                }
                conditionLogic={formData.entryCriteria.conditionLogic}
                onLogicChange={(logic) =>
                  setFormData({
                    ...formData,
                    entryCriteria: { ...formData.entryCriteria, conditionLogic: logic }
                  })
                }
                customLogic={formData.entryCriteria.customLogic}
                onCustomLogicChange={(logic) =>
                  setFormData({
                    ...formData,
                    entryCriteria: { ...formData.entryCriteria, customLogic: logic }
                  })
                }
                objectType={formData.objectType}
                description="The process will only run when these conditions are met"
              />
            )}

            {formData.triggerType.includes('updated') && (
              <div className="mt-4">
                <StandardCheckbox
                  label="Re-evaluate criteria on every update"
                  checked={formData.reevaluateOnUpdate}
                  onChange={(checked) => setFormData({ ...formData, reevaluateOnUpdate: checked })}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                  If unchecked, the process will only run when the criteria are newly met
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Decision Groups
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Create if/then branches to handle different scenarios
              </p>
            </div>

            <DecisionGroupEditor
              groups={formData.criteriaGroups}
              onChange={(groups) => {
                // Update immediateActions and scheduledActions to maintain consistency
                const newImmediateActions = { ...formData.immediateActions };
                const newScheduledActions = { ...formData.scheduledActions };

                // Add entries for new groups
                groups.forEach(g => {
                  if (g.id && !newImmediateActions[g.id]) {
                    newImmediateActions[g.id] = [];
                  }
                  if (g.id && !newScheduledActions[g.id]) {
                    newScheduledActions[g.id] = [];
                  }
                });

                setFormData({
                  ...formData,
                  criteriaGroups: groups,
                  immediateActions: newImmediateActions,
                  scheduledActions: newScheduledActions
                });
              }}
              objectType={formData.objectType}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Immediate Actions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Define what happens when each decision group matches
              </p>
            </div>

            {formData.criteriaGroups.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-3" />
                <p className="text-gray-600 dark:text-gray-400">
                  Please add decision groups first
                </p>
                <StandardButton
                  variant="secondary"
                  className="mt-4"
                  onClick={() => setCurrentStep(3)}
                >
                  Go to Decision Groups
                </StandardButton>
              </div>
            ) : (
              <>
                <StandardSelect
                  label="Select Decision Group"
                  value={selectedCriteriaGroup}
                  onChange={(e) => setSelectedCriteriaGroup(e.target.value)}
                  options={formData.criteriaGroups.map(g => ({
                    value: g.id || '',
                    label: g.name
                  }))}
                />

                {selectedCriteriaGroup && (
                  <ActionList
                    actions={formData.immediateActions[selectedCriteriaGroup] || []}
                    onChange={(actions) =>
                      setFormData({
                        ...formData,
                        immediateActions: {
                          ...formData.immediateActions,
                          [selectedCriteriaGroup]: actions
                        }
                      })
                    }
                    objectType={formData.objectType}
                    title={`Actions for "${formData.criteriaGroups.find(g => g.id === selectedCriteriaGroup)?.name}"`}
                    description="These actions execute immediately when this group's criteria match"
                  />
                )}
              </>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Scheduled Actions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Add time-based actions that execute after a delay
              </p>
            </div>

            {formData.criteriaGroups.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-3" />
                <p className="text-gray-600 dark:text-gray-400">
                  Please add decision groups first
                </p>
              </div>
            ) : (
              <>
                <StandardSelect
                  label="Select Decision Group"
                  value={selectedCriteriaGroup}
                  onChange={(e) => setSelectedCriteriaGroup(e.target.value)}
                  options={formData.criteriaGroups.map(g => ({
                    value: g.id || '',
                    label: g.name
                  }))}
                />

                {selectedCriteriaGroup && (
                  <ScheduledActionEditor
                    scheduledActions={formData.scheduledActions[selectedCriteriaGroup] || []}
                    onChange={(actions) =>
                      setFormData({
                        ...formData,
                        scheduledActions: {
                          ...formData.scheduledActions,
                          [selectedCriteriaGroup]: actions
                        }
                      })
                    }
                    objectType={formData.objectType}
                  />
                )}
              </>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Review & Activate
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Review your process configuration before saving
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Process Info */}
              <StandardCard>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Process Information
                </h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Name:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">{formData.name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Object:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">{formData.objectType}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Trigger:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {TRIGGER_TYPE_OPTIONS.find(t => t.value === formData.triggerType)?.label}
                    </dd>
                  </div>
                  {formData.category && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">Category:</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">{formData.category}</dd>
                    </div>
                  )}
                </dl>
              </StandardCard>

              {/* Summary Stats */}
              <StandardCard>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Configuration Summary
                </h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Entry Criteria:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {formData.hasEntryCriteria ? `${formData.entryCriteria.conditions.length} conditions` : 'None'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Decision Groups:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">{formData.criteriaGroups.length}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Immediate Actions:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {Object.values(formData.immediateActions).reduce((sum, a) => sum + a.length, 0)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Scheduled Actions:</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {Object.values(formData.scheduledActions).reduce((sum, a) => sum + a.length, 0)}
                    </dd>
                  </div>
                </dl>
              </StandardCard>
            </div>

            {/* Decision Groups Summary */}
            {formData.criteriaGroups.length > 0 && (
              <StandardCard>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Decision Groups
                </h4>
                <div className="space-y-3">
                  {formData.criteriaGroups.map((group, index) => (
                    <div key={group.id || index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 flex items-center justify-center font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{group.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {group.conditions.length} conditions •{' '}
                          {(formData.immediateActions[group.id || ''] || []).length} immediate actions •{' '}
                          {(formData.scheduledActions[group.id || ''] || []).length} scheduled
                        </div>
                      </div>
                      <CheckCircle className="text-green-500" size={20} />
                    </div>
                  ))}
                </div>
              </StandardCard>
            )}

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Ready to save
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    You can save as a draft to continue later, or activate immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Don't render if not open
  if (isOpen === false) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {resolvedProcessId ? 'Edit Process' : 'New Process'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {WIZARD_STEPS[currentStep - 1].description}
            </p>
          </div>
          <StandardButton variant="ghost" onClick={handleClose}>
            <X size={20} />
          </StandardButton>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={setCurrentStep}
        />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {renderStep()}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <StandardButton
            variant="secondary"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            leftIcon={<ChevronLeft size={16} />}
          >
            Previous
          </StandardButton>

          <div className="flex items-center gap-3">
            <StandardButton variant="ghost" onClick={handleClose}>
              Cancel
            </StandardButton>

            {currentStep === WIZARD_STEPS.length ? (
              <>
                <StandardButton
                  variant="secondary"
                  onClick={handleSaveDraft}
                  disabled={!formData.name || !formData.objectType || isLoading}
                  leftIcon={<Save size={16} />}
                >
                  Save as Draft
                </StandardButton>
                <StandardButton
                  variant="primary"
                  onClick={handleActivate}
                  disabled={!formData.name || !formData.objectType || formData.criteriaGroups.length === 0 || isLoading}
                  leftIcon={<Check size={16} />}
                >
                  Save & Activate
                </StandardButton>
              </>
            ) : (
              <StandardButton
                variant="primary"
                onClick={handleNext}
                disabled={!isStepValid(currentStep)}
                rightIcon={<ChevronRight size={16} />}
              >
                Next
              </StandardButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessBuilderWizard;
